import { Components, Theme } from '@mui/material'

declare module '@mui/material/Table' {
  interface TableOwnProps {
    /** Cells are compared across columns rather than read as rows: body cells take the vertical rule head cells have. */
    'data-bordered'?: boolean
  }
}

// Table (except TablePagination) is not used very much
// Most things are DataGrid, which is a separate component
export const MuiTable: Components<Omit<Theme, 'components'>>['MuiTable'] = {
  variants: [
    {
      props: { 'data-bordered': true },
      style: ({ theme }) => ({
        // Collapsed borders are painted by the table, not the cell, so a sticky cell would scroll out from
        // under its own rule.
        borderCollapse: 'separate',
        '& .MuiTableCell-body:not(:last-child)': {
          borderRightStyle: 'solid',
          borderRightWidth: theme.strokeWidth.default,
          borderRightColor: theme.palette.border.strong,
        },
      }),
    },
  ],
}

export const MuiTableContainer: Components<Omit<Theme, 'components'>>['MuiTableContainer'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      borderStyle: 'solid',
      borderWidth: theme.strokeWidth.default,
      borderColor: theme.palette.border.strong,

      borderRadius: theme.shape['radius-2xl'],
    }),
  },
}

export const MuiTableHead: Components<Omit<Theme, 'components'>>['MuiTableHead'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      backgroundColor: theme.palette.bg.surface.subtle,
    }),
  },
}

export const MuiTableBody: Components<Omit<Theme, 'components'>>['MuiTableBody'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      backgroundColor: theme.palette.bg.surface.default,
    }),
  },
}

export const MuiTableFooter: Components<Omit<Theme, 'components'>>['MuiTableFooter'] = {}

export const MuiTableRow: Components<Omit<Theme, 'components'>>['MuiTableRow'] = {}

declare module '@mui/material/TableCell' {
  interface TableCellProps {
    /** The viewer can act on this cell: it takes a pointer cursor and a hover tint. */
    'data-interactive'?: boolean
    /** Something outside the table is pointing at this cell — a chart hover naming the cell it came from. */
    'data-highlighted'?: boolean
  }
}

export const MuiTableCell: Components<Omit<Theme, 'components'>>['MuiTableCell'] = {
  defaultProps: {},
  styleOverrides: {
    root: ({ theme }) => ({
      padding: theme.spacing(3, 4),
      '--DataGrid-containerBackground': theme.palette.bg.surface.default,

      borderBottomStyle: 'solid',
      borderBottomWidth: theme.strokeWidth.default,
      borderBottomColor: theme.palette.border.strong,
      '.MuiTableRow-root:not(.MuiTableRow-head):last-child &': {
        borderBottomWidth: 0,
      },
    }),
    body: ({ theme }) => ({
      ...theme.typography['body-regular'],
      color: theme.palette.text.default,
    }),
    head: ({ theme }) => ({
      ...theme.typography['body-medium'],
      padding: theme.spacing(1.5, 1.5, 1.5, 4),
      color: theme.palette.text.muted,
      height: 40,

      borderRightStyle: 'solid',
      borderRightWidth: theme.strokeWidth.default,
      borderRightColor: theme.palette.border.strong,
      '&:last-child': {
        borderRightWidth: 0,
      },
    }),
  },
  variants: [
    {
      props: { 'data-interactive': true },
      style: ({ theme }) => ({
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: theme.palette.bg.interactive.hover,
        },
      }),
    },
    {
      props: { 'data-highlighted': true },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.interactive.hover,
      }),
    },
  ],
}

export const MuiTablePagination: Components<Omit<Theme, 'components'>>['MuiTablePagination'] = {
  defaultProps: {
    slotProps: {
      actions: {
        previousButton: {
          variant: 'ghost',
          size: 'small',
          color: 'secondary',
        },
        nextButton: {
          variant: 'ghost',
          size: 'small',
          color: 'secondary',
        },
        firstButton: {
          variant: 'ghost',
          size: 'small',
          color: 'secondary',
        },
        lastButton: {
          variant: 'ghost',
          size: 'small',
          color: 'secondary',
        },
      },
    },
  },
  styleOverrides: {
    root: ({ theme }) => ({
      // Bug in MUI select slot - styles get applied to nested element
      '& .MuiSelect-root': {
        marginRight: `calc(${theme.spacing(1.5)} + ${theme.spacing(2)})`,
        marginLeft: theme.spacing(1.5),
      },
    }),
    selectLabel: ({ theme }) => ({
      ...theme.typography['body-regular'],
      color: theme.palette.text.muted,
    }),
    displayedRows: ({ theme }) => ({
      ...theme.typography['body-regular'],
      color: theme.palette.text.muted,
      // Divider with spacing 2 separated by gap of 1.5
      paddingLeft: `calc(${theme.spacing(1.5)} + ${theme.spacing(2)})`,
      // Divider
      borderLeftWidth: theme.strokeWidth.default,
      borderLeftStyle: 'solid',
      borderLeftColor: theme.palette.border.subtle,
    }),
    actions: ({ theme }) => ({
      marginLeft: theme.spacing(1.5),
    }),
    selectIcon: () => ({
      marginRight: 0,
    }),
  },
}

export const MuiTableSortLabel: Components<Omit<Theme, 'components'>>['MuiTableSortLabel'] = {
  defaultProps: {},
  styleOverrides: {
    root: () => ({
      // TODO
    }),
  },
}
